import React, { useMemo, useCallback, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';

import { stringifyError } from '@/utils/errors';
import { Routes } from '@/core/router/routes.types';
import {
    useValidateActivationTokenQuery,
    useActivateAccountMutation,
    useRenewActivationTokenMutation,
} from '@/services/innovorder/user/user.endpoints';
import ActivateAccountPanel from './ActivateAccountPanel';
import logo from '../Login/logo.svg';
import { Header, Logo, ProductTitle } from '../Login/Login.style';

const ActivateAccountPage: React.FunctionComponent<React.PropsWithChildren> = () => {
    const location = useLocation();
    const history = useHistory();
    const token = useMemo(() => {
        const params = new URLSearchParams(location.search);
        return params.get('token') || '';
    }, [location.search]);

    const { data, error, isLoading } = useValidateActivationTokenQuery({ token }, { skip: !token });
    const [activateAccount, { isLoading: isSubmitting, error: submitError }] = useActivateAccountMutation();
    const [renewActivationToken, { isLoading: isRenewing }] = useRenewActivationTokenMutation();
    const [renewSuccess, setRenewSuccess] = useState(false);

    // Extract data from error response if present (for 400 errors that still contain isValid/canRenew)
    // When RTK Query receives a 400 error, it puts the response body in error.data
    // The API returns: { status: 400, code: "...", message: "...", data: { isValid, canRenew } }
    const errorData = React.useMemo(() => {
        if (!error || !('data' in error) || !error.data) {
            return null;
        }

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const errorResponse = error.data as any;

            // The API response has a nested 'data' property containing isValid and canRenew
            if (errorResponse && typeof errorResponse === 'object') {
                // First check for nested data structure: errorResponse.data (most common case)
                if (errorResponse.data && typeof errorResponse.data === 'object') {
                    const nestedData = errorResponse.data;
                    if ('isValid' in nestedData || 'canRenew' in nestedData) {
                        return {
                            isValid: Boolean(nestedData.isValid),
                            canRenew: Boolean(nestedData.canRenew),
                        };
                    }
                }
                // Fallback: check if isValid/canRenew are directly on errorResponse
                if ('isValid' in errorResponse || 'canRenew' in errorResponse) {
                    return {
                        isValid: Boolean(errorResponse.isValid),
                        canRenew: Boolean(errorResponse.canRenew),
                    };
                }
            }
        } catch (e) {
            // Silently fail if error parsing fails
        }
        return null;
    }, [error]);

    const handleSubmit = useCallback(
        async (payload: { password: string; confirmPassword: string }) => {
            if (!token) return;

            try {
                await activateAccount({
                    token,
                    password: payload.password,
                    confirmPassword: payload.confirmPassword,
                }).unwrap();
                // Redirect to login page on success
                history.push(Routes.Login);
            } catch (err) {
                // Error is handled by submitError
            }
        },
        [token, activateAccount, history],
    );

    const handleResend = useCallback(async () => {
        if (!token) return;

        try {
            await renewActivationToken({ token }).unwrap();
            // Show success message - new token has been sent by email
            setRenewSuccess(true);
        } catch (err) {
            // Error handling could be added here if needed
            setRenewSuccess(false);
        }
    }, [token, renewActivationToken]);

    // Extract data from error response if present (for 400 errors that still contain isValid/canRenew)
    const isValid = data?.isValid ?? errorData?.isValid ?? false;
    const canRenew = data?.canRenew ?? errorData?.canRenew ?? false;

    // Determine error message based on state
    const errorMessage = useMemo(() => {
        if (!token) return 'activateAccount.error.missingToken';
        if (error || !isValid) return 'activateAccount.error.invalidToken';
        return '';
    }, [token, error, isValid]);

    // Calculate panel props based on current state
    const panelProps = useMemo(() => {
        return {
            errorMessage,
            isLoading,
            passwordPolicy: data?.passwordPolicy,
            onSubmit: handleSubmit,
            submitError: stringifyError(submitError),
            isSubmitting,
            canRenew: Boolean(canRenew),
            onResend: handleResend,
            isRenewing,
            renewSuccess,
        };
    }, [
        errorMessage,
        isLoading,
        data?.passwordPolicy,
        handleSubmit,
        submitError,
        isSubmitting,
        canRenew,
        handleResend,
        isRenewing,
        renewSuccess,
    ]);

    return (
        <div>
            <Header>
                <Logo src={logo} />
                <ProductTitle>Backoffice</ProductTitle>
            </Header>
            <ActivateAccountPanel {...panelProps} />
        </div>
    );
};

export default ActivateAccountPage;
